import { isTaakAfgerond } from './parts/projectMeta';
import { useState, useMemo, useEffect, useRef } from 'react';
import { buildGrippProjectUrl, buildGrippOfferUrl } from '../../utils/grippDeepLink';
import { registreerOngedaanSneltoets } from '../../utils/ongedaan';
import toast from 'react-hot-toast';
import {
  Box,
  Typography,
  Drawer,
  Menu,
  MenuItem,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import TaskTemplateGenerator from './TaskTemplateGenerator';
import SmartTaskGenerator from './SmartTaskGenerator';
import ConceptScriptDialog, { isScriptV1, taakNaamVan } from '../ProjectFlow/ConceptScriptDialog';
import PreproductionWizard from './PreproductionWizard';
import WorkflowTemplateManager from './WorkflowTemplateManager';
import ProjectEditDialog from './ProjectEditDialog';


// Moment still used for date formatting
import moment from 'moment';
import 'moment/locale/nl';

import WijzigingslogKnop from '../../ui-components/WijzigingslogKnop';
import { T, Icon, stitchGradientBtn, stitchOutlineBtn, stitchSkeleton, StitchIconBtn } from './parts/stitch';
import { stilleTellers } from '../ProjectFlow/rustigeKop';

import TeamRegel from './parts/TeamRegel';
import TabPanel from './parts/TabPanel';
import DeliverableDetailDialog from './parts/DeliverableDetailDialog';
import TaakPaneel, { metOngedaan } from '../../ui-components/TaakPaneel/TaakPaneel';
import PlanningEmailDialog from './parts/PlanningEmailDialog';
import { ProjectDataProvider, useProjectData } from './ProjectDataContext';
import { BACKEND_URL, AI_ENGINE_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import ProjectFlowPage from '../ProjectFlow/ProjectFlowPage';
import RustigeProjectPagina from '../ProjectFlow/rustig/RustigeProjectPagina';
import { leesVoorkeur, bewaarVoorkeur, kiesWeergave as bepaalWeergave } from '../ProjectFlow/rustig/weergave';
import { PANEL_LABELS } from '../../config/projectFlow';
import ActivityPanel from './panels/ActivityPanel';
import TasksPanel from './panels/TasksPanel';
import DeliverablesPanel from './panels/DeliverablesPanel';
import DraaidagenPanel from './panels/DraaidagenPanel';
import QuitclaimsPanel from './panels/QuitclaimsPanel';
import CallsheetsPanel from './panels/CallsheetsPanel';
import NotesPanel from './panels/NotesPanel';
import MeetingsPanel from './panels/MeetingsPanel';
import DocumentsPanel from './panels/DocumentsPanel';
import FrameioPanel from './panels/FrameioPanel';
import QaPanel from './panels/QaPanel';
import CorrespondencePanel from './panels/CorrespondencePanel';
import OfferteregelsPanel from './panels/OfferteregelsPanel';
import ScriptPanel from './panels/ScriptPanel';
import UrenInkoopPanel from './panels/UrenInkoopPanel';
import { UrenBalk } from './list/onderdelen';
import { formatUren, urenFractie } from './list/projectGezondheid';
import KlantVereistenBlok from '../../ui-components/KlantVereistenBlok';

moment.locale('nl');

// Main Component: de provider laadt en beheert alle projectdata (zie
// ProjectDataContext.jsx); de content-component rendert de tabbladen en houdt
// alleen nog UI-state (actieve tab, weergaves, dialogs).
function ProjectOverview() {
  return (
    <ProjectDataProvider>
      <ProjectOverviewContent />
    </ProjectDataProvider>
  );
}

function ProjectOverviewContent() {
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();
  const {
    id,
    loading,
    voorvertoning,
    error,
    loadData,
    project,
    setProject,
    tasks,
    deliverables,
    setDeliverables,
    notes,
    transcripts,
    quitclaims,
    draaidagen,
    callsheets,
    frameioVideos,
    qaReviews,
    personen,
    quoteLines,
    pendingProposal,
    markProposal,
    selectedTask,
    setSelectedTask,
    setAutoEditTaskDate,
    handleTaskUpdate,
    handleTaskDelete,
    taakLokaalErbij,
    taakLokaalWeg,
    ouderLokaal,
    tasksPerDeliverable,
  } = useProjectData();

  // Ctrl/Cmd+Z = laatste afvink-/verplaats-actie terugdraaien (zie utils/ongedaan).
  useEffect(() => registreerOngedaanSneltoets(), []);

  // Waarnemend PL (actieve overdracht): niet de PL maar de waarnemer is nu
  // het aanspreekpunt — toon dat als chip in de header.
  const [waarneming, setWaarneming] = useState(null);
  useEffect(() => {
    if (!id) return;
    let actueel = true;
    authFetch(`${BACKEND_URL}/Api/overdracht/actief?project=${encodeURIComponent(id)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (actueel) setWaarneming(d?.overdracht || null); })
      .catch(() => {});
    return () => { actueel = false; };
  }, [id]);

  // Subtiele documenten-teller op de stat-tegel (het paneel laadt de rest); de rustige
  // weergave toont ook de naam van het nieuwste document.
  const [documenten, setDocumenten] = useState(null); // { aantal, laatste } | null
  // Het project dat nú openstaat (ook een oude undo-callback leest dit, nooit zijn eigen id).
  const docActueel = useRef(id);
  docActueel.current = id;
  const laadDocs = () => {
    const voor = docActueel.current;
    if (!voor) return;
    authFetch(`${BACKEND_URL}/Api/project-flow/${voor}/documents`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (docActueel.current !== voor || !d) return;
        const lijst = [...(d.documenten || [])].sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')));
        setDocumenten({ aantal: lijst.length, laatste: lijst[0] ? (lijst[0].naam || lijst[0].bestandsnaam || '') : '' });
      })
      .catch(() => {});
  };
  useEffect(() => {
    setDocumenten(null);
    laadDocs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  const docCount = documenten ? documenten.aantal : null;
  // Correspondentie-teller op de stat-tegel (Joris 18 sep): zelfde bron als de tab,
  // 180 dagen, alleen het aantal — de tab laadt de inhoud pas bij openen.
  const [mailLijst, setMailLijst] = useState(null);
  const mailCount = mailLijst ? mailLijst.length : null;
  // Ander project: de mails van het vorige horen er niet meer bij (ook niet tijdens het laden),
  // en een laat antwoord voor het vorige project wordt genegeerd.
  const mailVoor = useRef(id);
  mailVoor.current = id;
  useEffect(() => { setMailLijst(null); }, [id]);
  useEffect(() => {
    if (!project) return;
    // Alleen ophalen met de projectdata van dít project (tijdens een wissel staat het vorige er nog).
    if (project.id && project.id !== id && project.airtable_id !== id) return undefined;
    const voor = id;
    let actueel = true;
    const params = new URLSearchParams();
    const pn = project?.fields?.Projectnummer || project?.fields?.Projectnr || '';
    const naam = project?.fields?.Projectnaam || '';
    const em = project?.fields?.emailCP || [];
    const contact = Array.isArray(em) ? (em[0] || '') : String(em || '');
    if (pn) params.set('projectNumber', String(pn));
    if (naam) params.set('projectName', naam);
    if (contact) params.set('contactEmail', contact);
    params.set('days', '180');
    if (!pn && !naam && !contact) return undefined;
    authFetch(`${BACKEND_URL}/Api/mail-assistant/project-correspondence?${params}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (actueel && mailVoor.current === voor) setMailLijst(d ? (d.emails || []) : null); })   // mislukt = onbekend, niet 0
      .catch(() => { if (actueel && mailVoor.current === voor) setMailLijst(null); });
    return () => { actueel = false; };
  }, [project, id]);
  // Uren geschreven vs begroot (Gripp) voor de stille kopregel en het paneel
  // "Uren & inkoop" — één verzoek, beide lezen dezelfde stand.
  const [uren, setUren] = useState(null);
  const [urenLaden, setUrenLaden] = useState(true);
  useEffect(() => {
    if (!id) return undefined;
    let actueel = true;
    setUrenLaden(true);
    authFetch(`${BACKEND_URL}/Api/projects/${id}/uren`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (actueel) setUren(d); })
      .catch(() => { if (actueel) setUren(null); })
      .finally(() => { if (actueel) setUrenLaden(false); });
    return () => { actueel = false; };
  }, [id]);
  // Weergave: de flow is de startpagina; ?weergave=tabs geeft de oude
  // tabbladen terug (ontsnappingsluik) en ?paneel=<key> opent elk paneel als
  // drawer — geen enkel tabblad wordt onbereikbaar.
  const [searchParams, setSearchParams] = useSearchParams();
  // De keuze wordt onthouden (localStorage) zodat wie liever de tabbladen heeft
  // niet bij elk project opnieuw hoeft te schakelen; een expliciete
  // ?weergave=-parameter (deeplink) wint altijd.
  // Drie weergaven (rustig = nieuw, flow = vorige, tabs) — zie ProjectFlow/rustig/weergave.js.
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  const [weergaveVoorkeur, setWeergaveVoorkeur] = useState(() => {
    try { return leesVoorkeur(window.localStorage, host); } catch { return leesVoorkeur(null, host); }
  });
  const urlWeergave = searchParams.get('weergave');
  const weergave = bepaalWeergave({ url: urlWeergave, voorkeur: weergaveVoorkeur, host });
  const kiesWeergave = (w) => {
    setWeergaveVoorkeur(w);
    try { bewaarVoorkeur(window.localStorage, w); } catch { /* prima */ }
    setParam('weergave', null);
  };
  const paneel = searchParams.get('paneel');
  const setParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value === null) next.delete(key); else next.set(key, value);
    setSearchParams(next, { replace: false });
  };
  const openPaneel = (key) => setParam('paneel', key);
  // Sluiten haalt ook ?deliverable= weg (gemarkeerde deliverable, vanuit Vandaag).
  const closePaneel = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('paneel');
    next.delete('deliverable');
    setSearchParams(next, { replace: false });
  };

  // Paneel-render voor de drawer — zelfde componenten en props als de tabs.
  const renderPaneel = (key) => {
    switch (key) {
      case 'activiteit': return <ActivityPanel onOpenTab={openPaneel} />;
      case 'taken': return (
        <TasksPanel tasksView={tasksView} setTasksView={setTasksView}
          kanbanShowPLOnly={kanbanShowPLOnly} setKanbanShowPLOnly={setKanbanShowPLOnly}
          onOpenSmartTasks={() => setShowSmartTasks(true)}
          onOpenPreproWizard={() => setShowPreproWizard(true)} />
      );
      case 'deliverables': return (
        <DeliverablesPanel gemarkeerdId={searchParams.get('deliverable')}
          onOpenDetail={(item) => { setSelectedDeliverable(item); setShowDeliverableDetail(true); }}
          onGenerateTasks={(d) => { setSelectedDeliverable(d); setShowTaskGenerator(true); }} />
      );
      case 'draaidagen': return <DraaidagenPanel draaidagenView={draaidagenView} setDraaidagenView={setDraaidagenView} />;
      case 'quitclaims': return <QuitclaimsPanel />;
      case 'callsheets': return <CallsheetsPanel />;
      case 'notes': return <NotesPanel />;
      case 'meetings': return <MeetingsPanel />;
      case 'documenten': return <DocumentsPanel />;
      case 'frameio': return <FrameioPanel />;
      case 'qa': return <QaPanel />;
      case 'correspondentie': return <CorrespondencePanel active />;
      case 'offerteregels': return <OfferteregelsPanel />;
      case 'script': return <ScriptPanel />;
      case 'budget': return <UrenInkoopPanel projectId={id} uren={uren} urenLaden={urenLaden} />;
      default: return null;
    }
  };
  const [activeTab, setActiveTab] = useState('activiteit');
  const [draaidagenView, setDraaidagenView] = useState('list'); // 'calendar' or 'list'
  const [tasksView, setTasksView] = useState('phase'); // 'phase', 'grouped', 'list', 'kanban'
  const [kanbanShowPLOnly, setKanbanShowPLOnly] = useState(false); // Filter Kanban to show only PL tasks
  const [selectedDeliverable, setSelectedDeliverable] = useState(null); // For task generation
  const [showTaskGenerator, setShowTaskGenerator] = useState(false);
  const [showSmartTasks, setShowSmartTasks] = useState(false);
  const [conceptScript, setConceptScript] = useState(null); // { taakNaam? } — scriptgenerator-dialoog
  const [showPreproWizard, setShowPreproWizard] = useState(false);
  const [showTemplateManager, setShowTemplateManager] = useState(false);
  const [showDeliverableDetail, setShowDeliverableDetail] = useState(false); // For deliverable detail dialog
  const [showPlanningEmail, setShowPlanningEmail] = useState(false);
  const [showProjectEdit, setShowProjectEdit] = useState(false); // For project edit dialog
  // ⋯-menu in de kop: de minder gebruikte acties staan hier (rustiger kop).
  const [meerOpen, setMeerOpen] = useState(false);
  const meerKnopRef = useRef(null);
  const wijzigingslogOpen = useRef(null);
  const openInGripp = async () => {
    // Airtable-GrippID kan een offerte-id zijn — vraag eerst het échte
    // project-id op; zonder project valt de link terug op de offerte.
    let url = null;
    try {
      const nr = project?.fields?.Projectnummer;
      const r = nr ? await authFetch(`${AI_ENGINE_URL}/api/v1/gripp/project-id?number=${encodeURIComponent(nr)}`) : null;
      const d = r?.ok ? await r.json() : null;
      if (d?.gripp_project_id) url = buildGrippProjectUrl(d.gripp_project_id);
    } catch { /* fallback hieronder */ }
    if (!url) url = buildGrippOfferUrl(project?.fields?.GrippID);
    if (url) window.open(url, '_blank', 'noopener');
  };
  // Postproductieplanner vanaf de fase Postproductie (wiki 26 sep): de wizard
  // (TaskTemplateGenerator) werkt per deliverable en kan de planning daarna naar
  // de andere deliverables kopiëren. We openen hem op de eerste deliverable die
  // nog geen taken heeft; zonder deliverables eerst die aanmaken.
  const startPostprodPlanner = () => {
    const lijst = deliverables || [];
    if (!lijst.length) {
      toast('Maak eerst een deliverable aan — de planner plant per deliverable.', { icon: 'ℹ️' });
      openPaneel('deliverables');
      return;
    }
    const zonderTaken = lijst.find((d) => !(tasksPerDeliverable?.[d.id] > 0));
    setSelectedDeliverable(zonderTaken || lijst[0]);
    setShowTaskGenerator(true);
  };
  // Opstart-wizard hangt aan de flow (?opstart=1); vanuit de tabbladen eerst
  // naar de flow schakelen.
  const startOpstart = () => {
    const next = new URLSearchParams(searchParams);
    next.set('weergave', 'flow');   // een fase-weergave (rustig of vorige flow), nooit de tabbladen
    next.set('opstart', '1');
    setSearchParams(next, { replace: false });
  };

  // Project info
  const title = project?.fields?.Projectnr_en_naam || project?.fields?.Projectnaam || 'Project';
  const status = project?.fields?.Status || 'In progress';
  const type = project?.fields?.Projecttype || '';
  // Use lookup fields for actual names, fallback to raw field
  const clientNames = project?.fields?.KlantnaamString || project?.fields?.Klantnaam || [];
  const client = Array.isArray(clientNames) ? clientNames[0] : clientNames || '';
  // Team: zie parts/TeamRegel (rollen + namen, ook op develop waar alleen id's in de spiegel staan).

  // Stats
  const completedTasks = tasks.filter(isTaakAfgerond).length;

  // Tab definitions
  const tabDefs = useMemo(() => {
    const tabs = [
      { key: 'activiteit', icon: 'dynamic_feed', label: 'Activiteit' },
      { key: 'taken', icon: 'task_alt', label: `Taken (${tasks.length})` },
      { key: 'deliverables', icon: 'movie', label: `Deliverables (${deliverables.length})` },
      { key: 'draaidagen', icon: 'event', label: `Draaidagen (${draaidagen.length})` },
      { key: 'quitclaims', icon: 'description', label: `Quitclaims (${quitclaims.length})` },
      { key: 'callsheets', icon: 'phone', label: `Callsheets (${callsheets.length})` },
      { key: 'notes', icon: 'notes', label: `Notes (${notes.length})` },
      { key: 'meetings', icon: 'mic', label: `Meetings (${transcripts.length})` },
    ];
    if (frameioVideos.length > 0) {
      tabs.push({ key: 'frameio', icon: 'videocam', label: `Frame.io (${frameioVideos.length})` });
    }
    tabs.push({ key: 'qa', icon: 'bug_report', label: `QA Reviews${qaReviews.length ? ` (${qaReviews.length})` : ''}` });
    tabs.push({ key: 'correspondentie', icon: 'forum', label: 'Correspondentie' });
    tabs.push({ key: 'offerteregels', icon: 'request_quote', label: `Offerteregels${quoteLines?.count ? ` (${quoteLines.count})` : ''}` });
    tabs.push({ key: 'script', icon: 'description', label: 'Script' });
    tabs.push({ key: 'budget', icon: 'schedule', label: 'Uren & inkoop' });
    return tabs;
  }, [tasks.length, deliverables.length, draaidagen.length, quitclaims.length, callsheets.length, notes.length, transcripts.length, frameioVideos.length, qaReviews.length, quoteLines?.count]);

  if (loading) {
    // Vanuit de projectlijst kennen we naam, nummer en klant al: die kop staat er meteen,
    // alleen de rest laadt nog (Joris 27 sep: 'projectpagina laadt traag').
    const vvNaam = voorvertoning?.naam || '';
    const vvNr = voorvertoning?.nummer || '';
    const vvTitel = vvNr && !vvNaam.startsWith(vvNr) ? `${vvNr}. ${vvNaam}` : vvNaam;
    return (
      <Box sx={{ width: '100%', maxWidth: 1800, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 10 }}>
        {vvTitel ? (
          <Box sx={{ mb: 2.5 }} aria-busy="true">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
              <StitchIconBtn onClick={() => navigate('/projects')} title="Terug naar projecten">
                <Icon name="arrow_back" size={20} />
              </StitchIconBtn>
            </Box>
            <Typography variant="h5" sx={{ fontFamily: T.font, fontWeight: 700, color: T.text, letterSpacing: '-0.02em', mb: 0.5 }}>
              {vvTitel}
            </Typography>
            {voorvertoning.klant && !vvTitel.toLowerCase().includes(voorvertoning.klant.toLowerCase()) && (
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.85rem', color: T.outline }}>{voorvertoning.klant}</Typography>
            )}
          </Box>
        ) : (
          <>
            <Box sx={stitchSkeleton(300, 40)} />
            <Box sx={{ ...stitchSkeleton(200, 24), mt: 1, mb: 3 }} />
          </>
        )}
        <Box sx={stitchSkeleton('100%', 200)} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ width: '100%', maxWidth: 1800, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 10, textAlign: 'center' }}>
        <Typography sx={{ fontFamily: T.fontBody, color: T.error, mb: 2 }}>{error}</Typography>
        <Typography
          variant="body2"
          sx={{ fontFamily: T.fontBody, color: T.primary, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
          onClick={loadData}
        >
          Opnieuw proberen
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: 1800, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 10 }}>
      {/* De rustige projectpagina (nieuw, 23 okt): eigen kop, fasebalk en "Volgende stap".
          De panelen, dialogen en het taakpaneel hieronder deelt hij met de andere weergaven. */}
      {weergave === 'rustig' && (
        <RustigeProjectPagina
          acties={{
            bewerken: () => setShowProjectEdit(true),
            historie: (anker) => wijzigingslogOpen.current?.({ currentTarget: anker || meerKnopRef.current }),
            mail: () => setShowPlanningEmail(true),
            gripp: project?.fields?.GrippID ? openInGripp : null,
            ververs: () => { loadData(); laadDocs(); },
            weergave: kiesWeergave,
          }}
          onOpenPaneel={openPaneel}
          onOpenDialog={(key) => {
            if (key === 'smartTasks') setShowSmartTasks(true);
            else if (key === 'preproWizard') setShowPreproWizard(true);
            else if (key === 'conceptscript') setConceptScript({});
            else if (key === 'postprodPlanner') startPostprodPlanner();
          }}
          uren={uren}
          documenten={documenten}
          emails={mailLijst}
          waarneming={waarneming}
          onDocumentenGewijzigd={laadDocs}
          huidigProject={() => docActueel.current}
        />
      )}
      {/* Wijzigingslog: de popover hangt aan het ⋯-menu, geopend vanuit het menu (alle weergaven) */}
      <WijzigingslogKnop projectId={id} titel={project?.fields?.Projectnr_en_naam || project?.fields?.Projectnaam || ''}
        renderKnop={(open) => { wijzigingslogOpen.current = open; return null; }} />

      {weergave !== 'rustig' && (
      <>
      {/* Header — rustig (Joris, 25 sep): stille statusregel, één zichtbare
          actie (bewerken) + ⋯-menu, en alleen tellers met een waarde. */}
      <Box sx={{ mb: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, flexWrap: 'wrap' }}>
          <StitchIconBtn onClick={() => navigate('/projects')} title="Terug naar projecten">
            <Icon name="arrow_back" size={20} />
          </StitchIconBtn>
          <Typography variant="body2" sx={{ fontFamily: T.fontBody, fontSize: '0.85rem', color: T.outline }}>
            {[status, type].filter(Boolean).join(' · ')}
            {waarneming && (
              <Box component="span" title={`Overdracht van ${waarneming.van}`}>
                {' · '}waargenomen door {waarneming.naar}{waarneming.tot ? ` t/m ${new Date(waarneming.tot).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })}` : ''}
              </Box>
            )}
          </Typography>
          <Box sx={{ flex: 1 }} />
          {/* Zichtbare schakelaar tussen de flow en de vertrouwde tabbladen */}
          <Box sx={{ display: 'flex', gap: 0.25 }}>
            {[{ value: 'rustig', label: 'Nieuw' }, { value: 'flow', label: 'Flow' }, { value: 'tabs', label: 'Tabbladen' }].map((opt) => (
              <Box key={opt.value} component="button" type="button" onClick={() => kiesWeergave(opt.value)}
                aria-pressed={weergave === opt.value}
                sx={{
                  border: 0, bgcolor: 'transparent', px: 1, py: 0.5, cursor: 'pointer',
                  fontFamily: T.fontBody, fontSize: '0.85rem',
                  fontWeight: weergave === opt.value ? 700 : 500,
                  color: weergave === opt.value ? T.text : T.outline,
                  borderBottom: `2px solid ${weergave === opt.value ? T.text : 'transparent'}`,
                  '&:hover': { color: T.text },
                }}>
                {opt.label}
              </Box>
            ))}
          </Box>
          <StitchIconBtn onClick={() => setShowProjectEdit(true)} title="Project bewerken">
            <Icon name="edit" size={20} />
          </StitchIconBtn>
          <Box ref={meerKnopRef} sx={{ display: 'inline-flex' }}>
            <StitchIconBtn onClick={() => setMeerOpen(true)} title="Meer acties">
              <Icon name="more_horiz" size={20} />
            </StitchIconBtn>
          </Box>
          <Menu anchorEl={meerKnopRef.current} open={meerOpen} onClose={() => setMeerOpen(false)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{ paper: { sx: { borderRadius: '0.75rem', minWidth: 220 } } }}>
            {[
              { key: 'opstart', icon: 'rocket_launch', label: 'Project opstarten', actie: startOpstart },
              { key: 'mail', icon: 'mail', label: 'Mail planning naar klant', actie: () => setShowPlanningEmail(true) },
              { key: 'budget', icon: 'schedule', label: 'Uren & inkoop', actie: () => openPaneel('budget') },
              { key: 'log', icon: 'history', label: 'Wijzigingslog', actie: () => wijzigingslogOpen.current?.({ currentTarget: meerKnopRef.current }) },
              ...(project?.fields?.GrippID ? [{ key: 'gripp', icon: 'open_in_new', label: 'Open in Gripp', actie: openInGripp }] : []),
              { key: 'ververs', icon: 'refresh', label: 'Vernieuwen', actie: loadData },
            ].map((item) => (
              <MenuItem key={item.key} onClick={() => { setMeerOpen(false); item.actie(); }}
                sx={{ fontFamily: T.fontBody, fontSize: '0.85rem', gap: 1.25, color: T.text }}>
                <Icon name={item.icon} size={18} sx={{ color: T.outline }} />
                {item.label}
              </MenuItem>
            ))}
          </Menu>
        </Box>

        <Typography variant="h5" sx={{ fontFamily: T.font, fontWeight: 700, color: T.text, letterSpacing: '-0.02em', mb: 0.5 }}>
          {title}
        </Typography>

        {/* Eén stille regel: klant · tellers (alleen > 0, elk klikbaar); het team staat eronder */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
          fontFamily: T.fontBody, fontSize: '0.85rem', color: T.outline }}>
          {[
            // Klant alleen als die niet al in de projecttitel zit — anders dubbelop.
            // In de flow staat de klant al in de paspoortregel eronder.
            weergave !== 'flow' && client && !title.toLowerCase().includes(String(client).toLowerCase()) && { key: 'klant', label: client },
            ...stilleTellers({
              taken: tasks.length, takenAf: completedTasks, deliverables: deliverables.length,
              draaidagen: draaidagen.length, callsheets: callsheets.length, quitclaims: quitclaims.length,
              notes: notes.length, meetings: transcripts.length, documenten: docCount, correspondentie: mailCount,
            }).map((t) => ({ ...t, paneel: t.key })),
            // Uren geschreven/begroot met een klein balkje; klik = uren per persoon + inkoop.
            uren?.gekoppeld && uren.begroot != null && { key: 'uren', paneel: 'budget',
              label: (
                <Box component="span" title={`${formatUren(uren.geschreven)} van ${formatUren(uren.begroot)} u begroot (Gripp)`}
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                  <UrenBalk fractie={urenFractie(uren.begroot, uren.geschreven)} breedte={32} />
                  {formatUren(uren.geschreven)}/{formatUren(uren.begroot)} u
                </Box>
              ) },
          ].filter(Boolean).map((item, i) => (
            <Box key={item.key} component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
              {i > 0 && <Box component="span" aria-hidden sx={{ color: T.outlineVariant }}>·</Box>}
              {item.paneel ? (
                <Box component="span" role="button" tabIndex={0} onClick={() => openPaneel(item.paneel)}
                  onKeyDown={(e) => { if (e.key === 'Enter') openPaneel(item.paneel); }}
                  sx={{ cursor: 'pointer', '&:hover': { color: T.text, textDecoration: 'underline' } }}>
                  {item.label}
                </Box>
              ) : (
                <Box component="span" title={item.titel}>{item.label}</Box>
              )}
            </Box>
          ))}
        </Box>
        <TeamRegel fields={project?.fields} sx={{ mt: 0.5 }} />
      </Box>

      {/* Klaargezet taakvoorstel na acceptatie (draft-only) */}
      {pendingProposal && (
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
          p: 2, mb: 2, borderRadius: '0.75rem',
          bgcolor: T.tertiaryContainer + '55', border: `1px solid ${T.tertiary}55`,
        }}>
          <Icon name="checklist" size={22} sx={{ color: T.tertiary }} />
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography sx={{ fontFamily: T.font, fontWeight: 700, color: T.text, fontSize: '0.9rem' }}>
              Taakvoorstel klaar sinds acceptatie
            </Typography>
            <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.secondary }}>
              {pendingProposal.task_count} actiepunten voorgesteld uit de offerte — nog niet ingepland. Bekijk, pas aan en maak aan.
            </Typography>
          </Box>
          <Box component="span" onClick={() => { markProposal('applied'); setShowSmartTasks(true); }}
            sx={{ ...stitchGradientBtn, fontSize: '0.8rem' }}>
            <Icon name="auto_awesome" size={16} /> Openen
          </Box>
          <Box component="span" onClick={() => markProposal('dismissed')} sx={{ ...stitchOutlineBtn, fontSize: '0.8rem' }}>
            Later
          </Box>
        </Box>
      )}
      </>
      )}

      {/* Klantvereisten (bv. Schneider): rustig blok, alleen als het project bij een klantgroep hoort.
          De rustige weergave toont hem zelf in de werkkolom. */}
      {weergave !== 'rustig' && (
        <KlantVereistenBlok projectId={id} nummer={project?.fields?.Projectnummer} variant="project" />
      )}

      {/* Flow-weergave (standaard) — de fasenbalk als startpagina */}
      {weergave === 'flow' && (
        <ProjectFlowPage onOpenPaneel={openPaneel}
          onOpenDialog={(key) => {
            if (key === 'smartTasks') setShowSmartTasks(true);
            else if (key === 'preproWizard') setShowPreproWizard(true);
            else if (key === 'conceptscript') setConceptScript({});
            else if (key === 'postprodPlanner') startPostprodPlanner();
          }} />
      )}

      {/* Oude tabbladweergave via ?weergave=tabs */}
      {weergave === 'tabs' && (
      <>
      {/* Tab bar */}
      <Box sx={{ display: 'flex', gap: 0, overflowX: 'auto', borderBottom: `1px solid ${T.surfaceLow}`, mt: 1, mb: 2,
        '&::-webkit-scrollbar': { height: 0 },
      }}>
        {tabDefs.map((tab) => (
          <Box
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.6,
              px: 1.75,
              py: 1.25,
              cursor: 'pointer',
              fontFamily: T.fontBody,
              fontSize: '0.78rem',
              fontWeight: activeTab === tab.key ? 700 : 500,
              color: activeTab === tab.key ? T.tertiary : T.outlineVariant,
              borderBottom: activeTab === tab.key ? `2px solid ${T.tertiary}` : '2px solid transparent',
              transition: 'all 0.12s ease',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              '&:hover': activeTab === tab.key ? {} : { color: T.secondary, borderBottomColor: `${T.outlineVariant}40` },
            }}
          >
            <Icon name={tab.icon} size={16} sx={{ color: activeTab === tab.key ? T.tertiary : T.outlineVariant }} />
            {tab.label}
          </Box>
        ))}
      </Box>

      {/* Activity Feed Tab */}
      <TabPanel value={activeTab} index="activiteit">
        <ActivityPanel onOpenTab={setActiveTab} />
      </TabPanel>

      {/* Tasks Tab */}
      <TabPanel value={activeTab} index="taken">
        <TasksPanel
          tasksView={tasksView}
          setTasksView={setTasksView}
          kanbanShowPLOnly={kanbanShowPLOnly}
          setKanbanShowPLOnly={setKanbanShowPLOnly}
          onOpenSmartTasks={() => setShowSmartTasks(true)}
          onOpenPreproWizard={() => setShowPreproWizard(true)}
        />
      </TabPanel>

      {/* Deliverables Tab */}
      <TabPanel value={activeTab} index="deliverables">
        <DeliverablesPanel gemarkeerdId={searchParams.get('deliverable')}
          onOpenDetail={(item) => { setSelectedDeliverable(item); setShowDeliverableDetail(true); }}
          onGenerateTasks={(deliverable) => { setSelectedDeliverable(deliverable); setShowTaskGenerator(true); }}
        />
      </TabPanel>

      {/* Draaidagen Tab */}
      <TabPanel value={activeTab} index="draaidagen">
        <DraaidagenPanel draaidagenView={draaidagenView} setDraaidagenView={setDraaidagenView} />
      </TabPanel>

      {/* Quitclaims Tab */}
      <TabPanel value={activeTab} index="quitclaims">
        <QuitclaimsPanel />
      </TabPanel>

      {/* Callsheets Tab */}
      <TabPanel value={activeTab} index="callsheets">
        <CallsheetsPanel />
      </TabPanel>

      {/* Notes Tab */}
      <TabPanel value={activeTab} index="notes">
        <NotesPanel />
      </TabPanel>

      {/* Meetings Tab */}
      <TabPanel value={activeTab} index="meetings">
        <MeetingsPanel />
      </TabPanel>

      {/* Frame.io Videos Tab */}
      {frameioVideos.length > 0 && (
        <TabPanel value={activeTab} index="frameio">
          <FrameioPanel />
        </TabPanel>
      )}

      {/* QA Reviews Tab */}
      <TabPanel value={activeTab} index="qa">
        <QaPanel />
      </TabPanel>

      {/* Correspondentie Tab — alle klant-mails rond dit project */}
      <TabPanel value={activeTab} index="correspondentie">
        <CorrespondencePanel active={activeTab === 'correspondentie'} />
      </TabPanel>

      {/* Offerteregels Tab — wat er verkocht is, per sectie */}
      <TabPanel value={activeTab} index="offerteregels">
        <OfferteregelsPanel />
      </TabPanel>

      {/* Script uit SharePoint (alleen lezen; bewerken in Word) */}
      <TabPanel value={activeTab} index="script">
        <ScriptPanel />
      </TabPanel>

      {/* Uren (geschreven vs begroot) en inkoop */}
      <TabPanel value={activeTab} index="budget">
        <UrenInkoopPanel projectId={id} uren={uren} urenLaden={urenLaden} />
      </TabPanel>

      </>
      )}

      {/* Elk paneel als drawer vanuit de flow (?paneel=<key>) */}
      <Drawer anchor="right" open={!!paneel} onClose={closePaneel}
        PaperProps={{ sx: { width: { xs: '100vw', md: 900 }, maxWidth: '96vw', p: { xs: 2, md: 3 } } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography sx={{ fontFamily: T.font, fontWeight: 800, fontSize: '1.05rem', flex: 1 }}>
            {PANEL_LABELS[paneel] || paneel}
          </Typography>
          <StitchIconBtn onClick={closePaneel} title="Sluiten">
            <Icon name="close" size={20} />
          </StitchIconBtn>
        </Box>
        {paneel && renderPaneel(paneel)}
      </Drawer>

      {/* Taakdetail: het gedeelde rustige zijpaneel (de lijst blijft zichtbaar). */}
      <TaakPaneel
        taak={selectedTask}
        open={!!selectedTask}
        onClose={() => { setSelectedTask(null); setAutoEditTaskDate(false); }}
        onWijzig={metOngedaan(handleTaskUpdate)}
        onVerwijder={handleTaskDelete}
        personen={personen}
        // Subtaken: een subtaak of de ouder openen = die taak selecteren (dan werkt de lijst mee).
        onOpenTaak={(t) => setSelectedTask(tasks.find((x) => x.id === t.id) || { id: t.id, name: t.name })}
        onAangemaakt={taakLokaalErbij}
        onWeggehaald={taakLokaalWeg}
        onOuderGewijzigd={(taakId, nieuw) => ouderLokaal(taakId, nieuw)}
        extraActies={isScriptV1(selectedTask) ? [{ label: 'Concept maken', onClick: () => setConceptScript({ taakNaam: taakNaamVan(selectedTask) }) }] : []}
      />

      {/* Conceptscript (scriptgenerator): vanuit Gereedschap of de taak "Script V1" */}
      <ConceptScriptDialog
        open={!!conceptScript}
        onClose={() => { setConceptScript(null); laadDocs(); }}
        projectId={project?.airtable_id || project?.id}
        taakNaam={conceptScript?.taakNaam}
      />

      {/* Task Template Generator Dialog */}
      <TaskTemplateGenerator
        open={showTaskGenerator}
        onClose={() => {
          setShowTaskGenerator(false);
          setSelectedDeliverable(null);
        }}
        deliverable={selectedDeliverable}
        alleDeliverables={deliverables}
        draaidagen={draaidagen}
        projectId={id}
        project={project}
        onSuccess={() => {
          // Op de achtergrond verversen: het laadscherm zou de wizard (en het
          // resultaat dat hij nog toont) unmounten.
          loadData({ achtergrond: true });
          // NB: schakelde historisch naar de Activiteit-tab (index 0), al zegt de
          // oude comment 'Tasks tab' — gedrag bewust behouden bij de key-migratie.
          setActiveTab('activiteit');
          setTasksView('timeline');
        }}
      />

      {/* Slimme taken op basis van de offerte (lokale offerteregels via de engine) */}
      <SmartTaskGenerator
        open={showSmartTasks}
        onClose={() => setShowSmartTasks(false)}
        projectId={project?.airtable_id || project?.id}
        projectName={project?.fields?.Projectnaam || project?.name || ''}
        onCreated={() => {
          loadData();
          setActiveTab('taken');
        }}
      />

      {/* Preproduction Wizard */}
      <PreproductionWizard
        open={showPreproWizard}
        onClose={() => {
          setShowPreproWizard(false);
          loadData();
        }}
        project={project}
        onOpenTemplateManager={() => {
          setShowPreproWizard(false);
          setShowTemplateManager(true);
        }}
      />

      {/* Workflow Template Manager */}
      <WorkflowTemplateManager
        open={showTemplateManager}
        onClose={() => setShowTemplateManager(false)}
      />

      {/* Deliverable Detail Dialog */}
      <DeliverableDetailDialog
        open={showDeliverableDetail}
        onClose={() => {
          setShowDeliverableDetail(false);
          setSelectedDeliverable(null);
        }}
        deliverable={selectedDeliverable}
        taskCount={selectedDeliverable ? (tasksPerDeliverable[selectedDeliverable.id] || 0) : 0}
        onGenerateTasks={(deliverable) => {
          setShowDeliverableDetail(false);
          setSelectedDeliverable(deliverable);
          setShowTaskGenerator(true);
        }}
        onUpdate={(updatedDeliverable) => {
          // Update the deliverables list with the updated data
          setDeliverables(prev => prev.map(d =>
            d.id === updatedDeliverable.id ? updatedDeliverable : d
          ));
          setSelectedDeliverable(updatedDeliverable);
        }}
      />

      {/* Planning Email Dialog */}
      {showPlanningEmail && (
        <PlanningEmailDialog
          open={showPlanningEmail}
          onClose={() => setShowPlanningEmail(false)}
          project={project}
          deliverables={deliverables}
          tasks={tasks}
          msalInstance={instance}
          account={accounts?.[0]}
          senderName={accounts?.[0]?.name || ''}
        />
      )}

      {/* Project Edit Dialog */}
      <ProjectEditDialog
        open={showProjectEdit}
        onClose={() => setShowProjectEdit(false)}
        project={project}
        notes={notes}
        deliverables={deliverables}
        tasks={tasks}
        draaidagen={draaidagen}
        onVeldBijgewerkt={setProject}
        onDeliverableUpdate={(bijgewerkt) => setDeliverables(prev => prev.map(d => (d.id === bijgewerkt.id ? bijgewerkt : d)))}
        onUpdate={(updatedProject) => {
          setProject(updatedProject);
          setShowProjectEdit(false);
        }}
        onNotesUpdate={loadData}
      />
    </Box>
  );
}

export default ProjectOverview;
